import React from 'react';
import { Dispatch } from 'hellospa/redux/types';
import { useDispatch } from 'react-redux';
import * as actions from 'hellospa/page/prep-and-send/data/actions';
import { ChooseSource } from './choose-source';
import { openContactImport, Waiting } from './waiting';
import { InviteFriends } from './invite-friends';
import { ThankYou } from './thank-you';
import { trackHeapCustomEvent } from 'common/utils/heap';


export enum Source {
  Gmail = 'gmail',
  Yahoo = 'yahoo'
}


type Props = {
  isOpen: boolean
  onRequestClose: () => void
  isHelloFax: boolean
}

type Step1 = {
  step: 1,
  source?: Source
}

type Step2 = {
  step: 2,
  source: Source
}

type Step3 = {
  step: 3,
  source: Source
}
type Step4 = {
  step: 4,
  source: Source
}

type State = Step1 | Step2 | Step3 | Step4

// The Yahoo import is broken.
const skipYahoo = true;

export default function ImportModal({ isOpen, onRequestClose, isHelloFax }: Props) {
  const dispatch = useDispatch<Dispatch>();

  const [state, setState] = React.useState<State>({ step: 1 });

  const startImport = React.useCallback(async (source: Source) => {
    openContactImport(source);

    setState({
      step: 2,
      source,
    });
  }, []);

  const { step } = state;
  React.useEffect(() => {
    // Any time the modal closes, reset it.
    if (!isOpen) {
      setState({ step: 1 });
    } else if (step === 1) {
      trackHeapCustomEvent('Import Contacts Modal');
      if (skipYahoo) {
        startImport(Source.Gmail);
      }

      // Refresh the contacts when this loads.
      dispatch(actions.loadContacts(true));
    }
  }, [dispatch, isOpen, startImport, step]);

  const waitingNextStep = React.useCallback(() => {
    setState((state) => {
      // This just assures TS that a source has been set
      if (state.step === 2) {
        return {
          ...state,
          step: 3,
        };
      }
      return state;
    });
  }, []);

  if (!isOpen) {
    return null;
  }
  switch (state.step) {
    case 1: {
      return (
        <ChooseSource
          onRequestClose={onRequestClose}
          nextStep={startImport} />
      );
    }
    case 2: {
      return (<Waiting
        source={state.source}
        onRequestClose={onRequestClose}
        nextStep={waitingNextStep} />);
    }
    case 3: {
      return <InviteFriends
        isHelloFax={isHelloFax}
        onRequestClose={onRequestClose}
        nextStep={() => {
          setState((state) => {
            // This just assures TS that a source has been set
            if (state.step === 3) {
              return {
                ...state,
                step: 4,
              };
            }
            return state;
          });
        }} />;
    }
    case 4: {
      return <ThankYou
        onRequestClose={onRequestClose}
        isHelloFax={isHelloFax} />;
    }
    default:
  }

  return null;

}
